import { api } from "@utils/api";
import { firstAccessiblePath } from "@utils/permissions";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, PasswordInput, TextInput } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useDocumentTitle } from "@mantine/hooks";
import { IconLock } from "@tabler/icons-react";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "src/contexts/auth";
import i18n from "src/i18n";
import { z } from "zod";

import { EnvAlert } from "@components/EnvAlert";
import { LanguageToggle } from "@components/LanguageToggle";

import { showErrorNotification } from "@utils/showNotification";

import { PAGE_TITLE, ROLES } from "@constants/index";

import { useSignIn } from "@hooks/mutations/user/useSignIn";

import Logo from "@assets/misc/logo.webp";

import { StyledSignIn } from "./SignIn.styled";


const schema = () =>
  z.object({
    username: z.string().min(1, i18n.t("username-required")),
    password: z.string().min(1, i18n.t("password-required")),
  });

type Inputs = z.infer<ReturnType<typeof schema>>;

export const SignIn = () => {
  const { t } = useTranslation();
  useDocumentTitle(`${t("login")} • ${PAGE_TITLE}`);

  const navigate = useNavigate();
  const { state } = useLocation();

  const { setToken, setUsername, setEmail, setFullName, setId, setRoles, setOnboardingStatus } =
    useAuth();

  const { mutate: login, isLoading } = useSignIn();

  const form = useForm({
    mode: "uncontrolled",
    validate: zodResolver(schema()),
    initialValues: {
      username: "",
      password: "",
    },
  });

  const handleFormSubmit = (values: Inputs) => {
    // console.log(values);
    login(values, {
      onSuccess: async (res: any) => {
        const token = res.data.access_token;
        const decoded: any = jwtDecode(token);
        setToken(token);
        setId(decoded.id);
        setFullName(decoded.full_name);
        setUsername(decoded.username);
        setEmail(decoded.email);
        setRoles(decoded.roles);
        setOnboardingStatus(decoded.onboarding_status || "approved");

        const roles = decoded.roles;
        const onboardingStatus = decoded.onboarding_status || "approved";

        if (roles.includes("doctor") && onboardingStatus !== "approved") {
          navigate("/onboard");
          return;
        }

        // todo: Change state to query param?
        if (state?.redirectURL) {
          // console.log("inside redirect:", state.redirectURL);
          navigate(state.redirectURL);
          return;
        }
        try {
          const { data } = await api.get("access/me", { headers: { Authorization: `Bearer ${token}` } });
          navigate(firstAccessiblePath(data.permissions));
        } catch {
          navigate("/patients");
        }
      },
    });
  };

  useEffect(() => {
    if (state?.redirectURL) {
      // console.log("redirect to:", state.redirectURL);
      showErrorNotification({
        title: "redirect-after-login-title",
        message: "redirect-after-login-message",
      });
    } else if (state?.username) {
      // setValue("username", state.username);
      form.setValues({ username: state.username });
    }
  }, []);

  return (
    <StyledSignIn>
      <LanguageToggle />

      <div className="content">
        <div className="logo">
          <img src={Logo} alt="La Ruche Health" />
        </div>
        <h1 className="heading">{t("welcome")}</h1>
        <p className="sub-heading">{t("emr-portal")}</p>
        <EnvAlert />
        <form
          className="form"
          onSubmit={form.onSubmit(
            values => handleFormSubmit(values),
            errors => {
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}>
          <TextInput
            label={t("username")}
            withAsterisk
            placeholder={t("eg", { example: "John Doe" })}
            {...form.getInputProps("username")}
            key={form.key("username")}
          />

          <PasswordInput
            label={t("password")}
            withAsterisk
            leftSection={<IconLock size={16} />}
            {...form.getInputProps("password")}
            key={form.key("password")}
          />
          <Button type="submit" mt={"lg"} loading={isLoading}>
            {t("login")}
          </Button>
        </form>
        <Link to="/forgot-password" className="forgot-password hover-underline">
          {`${t("forgot-password")}?`}
        </Link>
        <a
          href="https://emr.laruche.health/privacypolicy"
          className="hover-underline"
          style={{ display: "block", marginTop: "1rem", textAlign: "center", fontSize: "0.875rem", color: "var(--mantine-color-dimmed)" }}
        >
          {t("privacy-policy")}
        </a>
      </div>
    </StyledSignIn>
  );
};
